import { useState, useRef, useEffect, useCallback } from 'react';
import { Trash2, Send, Smile, BriefcaseBusiness, Zap, ShieldAlert, Globe2, CircuitBoard, BookOpen, SlidersHorizontal, MessageSquareText, ChevronDown } from 'lucide-react';
import { fetchApi } from '../api';
import { formatMessageHTML } from '../utils';
import '../styles/ai-agent.css';

/**
 * The AI agent screen.
 *
 * This screen used to assert that "Clientix" is an AI sales manager "обучен на вашем
 * каталоге" and offered a switch labelled "Использовать внешний ИИ" whose only
 * explanation was a single line. In the shipped configuration the external model
 * is off, so the agent answers from a local rule engine — and nothing on the
 * screen said so. The status panel below is now derived entirely from
 * `GET /ai/runtime-status`: it names which of the three gates is closed, what to
 * do about it, and, when the external model is on, which provider host receives
 * customer messages and when consent to that was recorded.
 */

interface RuntimeStatus {
  mode: 'external' | 'local';
  reason: string | null;
  blockedGate: 'provider' | 'operator' | 'organization' | null;
  llmBacked: boolean;
  title: string;
  explanation: string;
  actionHint: string;
  operatorSharingEnabled: boolean;
  providerConfigured: boolean;
  organizationConsented: boolean;
  consentAcceptedAt: string | null;
  consentAcceptedBy: number | null;
  consentAvailable: boolean;
  provider: { host: string | null; model: string | null } | null;
  transmitted: string[];
  plan: {
    effectivePlan: string;
    effectivePlanName: string;
    knowledgeBase: boolean;
    advancedRag: boolean;
    aiMessagesUsed: number;
    aiMessagesLimit: number;
    aiMessagesLeft: number;
    currentPeriodEnd: string;
    downgraded: boolean;
  };
  embeddings: { enabled: boolean; disabledReason: string | null; endpoint: string | null; model: string };
}

const formatDate = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('ru-RU') : null);

export default function AiAgent({ autopilot, setAutopilot }: { autopilot: boolean, setAutopilot: (val: boolean) => void }) {
  const [kbItems, setKbItems] = useState<any[]>([]);
  const [kbInput, setKbInput] = useState('');
  const [tone, setTone] = useState('дружелюбный');
  const [settings, setSettings] = useState<any>({});
  const [stats, setStats] = useState<any>(null);
  const [runtime, setRuntime] = useState<RuntimeStatus | null>(null);
  const [kbIndex, setKbIndex] = useState<Record<string, number> | null>(null);
  const [consentError, setConsentError] = useState<string | null>(null);
  const [consentBusy, setConsentBusy] = useState(false);
  const [pgMessages, setPgMessages] = useState<{ from: 'user' | 'bot', text: string }[]>([]);
  const [pgInput, setPgInput] = useState('');
  const pgMessagesRef = useRef<HTMLDivElement>(null);
  const pgEndRef = useRef<HTMLDivElement>(null);

  const loadRuntime = useCallback(() => (
    fetchApi('/ai/runtime-status')
      .then((data: RuntimeStatus) => {
        setRuntime(data);
        return data;
      })
      .catch(() => null)
  ), []);

  useEffect(() => {
    fetchApi('/ai/settings').then(s => { setSettings(s); setTone(s.tone || 'дружелюбный'); setAutopilot(!!s.autopilot); }).catch(console.error);
    fetchApi('/ai/kb').then(data => setKbItems(data)).catch(console.error);
    fetchApi('/ai/kb/status').then(data => setKbIndex(data)).catch(() => setKbIndex(null));
    fetchApi('/stats/dashboard').then(data => setStats(data)).catch(console.error);
    void loadRuntime();
  }, [setAutopilot, loadRuntime]);

  // The greeting must not promise a language model when a rule engine answers.
  useEffect(() => {
    if (!runtime) return;
    setPgMessages(prev => (prev.length > 0 ? prev : [{
      from: 'bot',
      text: runtime.mode === 'external'
        ? 'Привет! Я Clientix. Отвечаю через внешнюю языковую модель — как и вашим клиентам. Спросите про доставку, скидки, наличие или оплату.'
        : 'Привет! Я Clientix. Сейчас я отвечаю по локальным правилам, без внешней языковой модели: ищу товары в каталоге, факты в базе знаний и оформляю заявки. Проверьте на вопросе про доставку, скидку, наличие или оплату.',
    }]));
  }, [runtime]);

  useEffect(() => {
    const messages = pgMessagesRef.current;
    if (messages) messages.scrollTo({ top: messages.scrollHeight, behavior: 'smooth' });
  }, [pgMessages]);

  const updateSetting = (key: string, value: any) => {
    let camelKey = key;
    let apiKey = key;
    if (key === 'night_mode' || key === 'nightMode') { camelKey = 'nightMode'; apiKey = 'night_mode'; }
    else if (key === 'discount_limit' || key === 'discountLimit') { camelKey = 'discountLimit'; apiKey = 'discount_limit'; }
    setSettings((prev: any) => ({ ...prev, [camelKey]: value }));
    fetchApi('/ai/settings', { method: 'PATCH', body: JSON.stringify({ [apiKey]: value }) }).catch(console.error);
  };

  /**
   * Consent is not an ordinary switch: turning it on starts sending end-customer
   * messages to a third party. It is awaited, the refusal is shown, and the
   * status panel is reloaded so the screen and the backend cannot disagree.
   */
  const setDataSharing = async (value: boolean) => {
    setConsentBusy(true);
    setConsentError(null);
    try {
      const updated = await fetchApi('/ai/settings', {
        method: 'PATCH',
        body: JSON.stringify({ ai_data_sharing: value }),
      });
      setSettings(updated);
      setTone(updated.tone || tone);
      await loadRuntime();
    } catch (error: any) {
      setConsentError(error?.message || 'Не удалось изменить согласие.');
      await loadRuntime();
    } finally {
      setConsentBusy(false);
    }
  };

  const addKb = () => {
    if (!kbInput.trim()) return;
    fetchApi('/ai/kb', { method: 'POST', body: JSON.stringify({ text: kbInput.trim() }) })
      .then(newKb => {
        setKbItems([...kbItems, newKb]);
        setKbInput('');
        fetchApi('/ai/kb/status').then(setKbIndex).catch(() => {});
      })
      .catch(console.error);
  };

  const removeKb = (id: number) => {
    fetchApi(`/ai/kb/${id}`, { method: 'DELETE' }).then(() => setKbItems(kbItems.filter(k => k.id !== id))).catch(console.error);
  };

  const pgSend = () => {
    if (!pgInput.trim()) return;
    const v = pgInput.trim();
    setPgMessages(prev => [...prev, { from: 'user', text: v }]);
    setPgInput('');
    fetchApi('/ai/playground', { method: 'POST', body: JSON.stringify({ text: v }) })
      .then(res => {
        // An internal outcome (a plan limit, for instance) carries no customer
        // text by construction. Show the operational reason instead of pretending
        // the agent said something.
        const text = res?.kind === 'internal'
          ? `⚠️ Агент не ответил. ${res.incident?.ownerMessage || 'Ограничение тарифа.'}`
          : `✦ ${res.text}`;
        setPgMessages(prev => [...prev, { from: 'bot', text }]);
      })
      .catch((error: any) => {
        setPgMessages(prev => [...prev, { from: 'bot', text: `⚠️ ${error?.message || 'Не удалось получить ответ.'}` }]);
      });
  };

  const isDiscountEnabled = settings.discountLimit !== undefined && settings.discountLimit > 0;
  const local = !runtime || runtime.mode === 'local';
  const consentDate = formatDate(runtime?.consentAcceptedAt ?? null);
  const kbReady = kbIndex?.ready ?? 0;
  const kbPending = (kbIndex?.pending ?? 0) + (kbIndex?.failed ?? 0);

  // Subtitle of the hero card. "Обучен на вашем каталоге" is a claim about a
  // trained model, so it is only shown when a model is genuinely in the loop.
  const heroRole = runtime
    ? (runtime.mode === 'external'
      ? 'Отвечает внешней языковой моделью, опираясь на ваш каталог, склад и историю сделок'
      : 'Отвечает по локальным правилам, опираясь на ваш каталог, склад и базу знаний')
    : 'Загружаем режим работы агента…';

  return (
    <section className="view active ai-page" id="view-ai">
      <header className="ai-command-bar">
        <div className="ai-command-main">
          <div className="ai-command-identity">
            <div className="ai-command-avatar" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M12 3l1.9 4.6 4.6 1.9-4.6 1.9L12 16l-1.9-4.6L5.5 9.5l4.6-1.9L12 3z" />
                <path d="M19 14.5l.9 2.1 2.1.9-2.1.9-.9 2.1-.9-2.1-2.1-.9 2.1-.9.9-2.1z" />
              </svg>
            </div>
            <div>
              <div className="ai-command-eyebrow">Цифровой сотрудник</div>
              <h1>ИИ-агент Clientix</h1>
              <p>{heroRole}</p>
            </div>
          </div>
          <div className="ai-command-state">
            <span className={`ai-live-dot ${autopilot ? 'is-on' : ''}`} aria-hidden="true" />
            <div>
              <b>{autopilot ? 'Агент отвечает клиентам' : 'Агент на паузе'}</b>
              <span>{autopilot ? 'Во всех подключённых каналах' : 'Диалоги ждут менеджера'}</span>
            </div>
          </div>
          <label className="ai-autopilot-control">
            <span><b>Автопилот</b><small>{autopilot ? 'Включён' : 'Выключен'}</small></span>
            <span className="switch">
              <input type="checkbox" className="ap-switch" checked={autopilot} onChange={(event) => {
                setAutopilot(event.target.checked);
                updateSetting('autopilot', event.target.checked);
              }} />
              <span className="slider" />
            </span>
          </label>
        </div>

        <div className="ai-command-metrics" aria-label="Показатели ИИ-агента">
          <div><span>Диалоги сегодня</span><b>{stats ? stats.leadsToday : '—'}</b></div>
          <div><span>Ответы агента</span><b>{stats ? stats.aiMessagesToday : '—'}</b></div>
          <div><span>Конверсия</span><b>{stats ? `${stats.conversion}%` : '—'}</b></div>
          <div><span>Выручка</span><b>{stats ? `${stats.revenueToday.toLocaleString('ru-RU')} ₽` : '—'}</b></div>
          <div className="ai-quota-metric">
            <span>Остаток ИИ-ответов</span>
            <b>{runtime ? runtime.plan.aiMessagesLeft.toLocaleString('ru-RU') : '—'}</b>
            {runtime && <small>из {runtime.plan.aiMessagesLimit.toLocaleString('ru-RU')}</small>}
          </div>
        </div>

        {runtime && (
          <details className={`ai-runtime-details ${local ? 'is-local' : 'is-external'}`}>
            <summary>
              <span className="ai-runtime-summary-icon">
                {local ? <CircuitBoard size={15} aria-hidden="true" /> : <Globe2 size={15} aria-hidden="true" />}
              </span>
              <span className="ai-runtime-summary-copy">
                <b>{runtime.title}</b>
                <small>
                  Тариф «{runtime.plan.effectivePlanName}»
                  {runtime.provider?.host ? ` · ${runtime.provider.host}` : ''}
                  {runtime.plan.currentPeriodEnd ? ` · до ${formatDate(runtime.plan.currentPeriodEnd)}` : ''}
                </small>
              </span>
              <span className="ai-runtime-more">Подробнее <ChevronDown size={14} aria-hidden="true" /></span>
            </summary>
            <div className="ai-runtime-expanded">
              <p>{runtime.explanation}</p>
              {runtime.actionHint && <p>{runtime.actionHint}</p>}
              {runtime.plan.downgraded && <p>Оплаченный период закончился — сейчас действуют лимиты тарифа «{runtime.plan.effectivePlanName}».</p>}
            </div>
          </details>
        )}
      </header>

      <div className="ai-workspace">
        <section className="ai-panel ai-behavior-panel" aria-labelledby="ai-behavior-title">
          <header className="ai-panel-head">
            <span className="ai-panel-icon"><SlidersHorizontal size={17} aria-hidden="true" /></span>
            <div><h2 id="ai-behavior-title">Поведение</h2><p>Как агент общается и продаёт</p></div>
          </header>
          <div className="ai-panel-body">
            <fieldset className="ai-fieldset">
              <legend>Тон общения</legend>
              <div className="tone-chips" role="group" aria-label="Тон общения">
                <button type="button" aria-pressed={tone === 'дружелюбный'} className={`tone-chip ${tone === 'дружелюбный' ? 'active' : ''}`} onClick={() => { setTone('дружелюбный'); updateSetting('tone', 'дружелюбный'); }}><Smile size={15} aria-hidden="true" /> Дружелюбный</button>
                <button type="button" aria-pressed={tone === 'деловой'} className={`tone-chip ${tone === 'деловой' ? 'active' : ''}`} onClick={() => { setTone('деловой'); updateSetting('tone', 'деловой'); }}><BriefcaseBusiness size={15} aria-hidden="true" /> Деловой</button>
                <button type="button" aria-pressed={tone === 'краткий'} className={`tone-chip ${tone === 'краткий' ? 'active' : ''}`} onClick={() => { setTone('краткий'); updateSetting('tone', 'краткий'); }}><Zap size={15} aria-hidden="true" /> Краткий</button>
              </div>
            </fieldset>

            <div className="ai-section-label">Правила продаж</div>
            <div className="ai-rule-list">
              <label className="ai-rule">
                <span><b>Передавать при негативе</b><small>Сразу подключит менеджера</small></span>
                <span className="switch"><input type="checkbox" checked={!!settings.escalation} onChange={event => updateSetting('escalation', event.target.checked)} /><span className="slider" /></span>
              </label>
              <label className="ai-rule">
                <span><b>Предлагать допродажу</b><small>Подберёт сопутствующие товары</small></span>
                <span className="switch"><input type="checkbox" checked={!!settings.upsell} onChange={event => updateSetting('upsell', event.target.checked)} /><span className="slider" /></span>
              </label>
              <div className="ai-rule ai-rule-discount">
                <label>
                  <span><b>Разрешить скидки</b><small>{isDiscountEnabled ? `Не более ${settings.discountLimit}%` : 'Скидки отключены'}</small></span>
                  <span className="switch"><input type="checkbox" checked={isDiscountEnabled} onChange={event => updateSetting('discountLimit', event.target.checked ? 15 : 0)} /><span className="slider" /></span>
                </label>
                {isDiscountEnabled && (
                  <label className="ai-discount-input">
                    <span>Лимит</span>
                    <input type="number" min="1" max="50" value={settings.discountLimit || 15} aria-label="Максимальная скидка в процентах" onChange={event => updateSetting('discountLimit', Math.min(50, Math.max(0, parseInt(event.target.value) || 0)))} />
                    <span>%</span>
                  </label>
                )}
              </div>
              <label className="ai-rule">
                <span><b>Ночной режим</b><small>Отвечает и собирает контакты с 22:00 до 09:00</small></span>
                <span className="switch"><input type="checkbox" checked={!!settings.nightMode} onChange={event => updateSetting('nightMode', event.target.checked)} /><span className="slider" /></span>
              </label>
            </div>
          </div>
        </section>

        <section className="ai-panel ai-knowledge-panel" aria-labelledby="ai-knowledge-title">
          <header className="ai-panel-head">
            <span className="ai-panel-icon"><BookOpen size={17} aria-hidden="true" /></span>
            <div><h2 id="ai-knowledge-title">Знания и данные</h2><p>{kbItems.length ? `${kbItems.length} ${kbItems.length === 1 ? 'факт' : 'факта'} добавлено` : 'Добавьте первый факт'}</p></div>
          </header>
          <div className="ai-panel-body">
            <form className="ai-kb-form" onSubmit={(event) => { event.preventDefault(); addKb(); }}>
              <label htmlFor="ai-kb-input">Новый факт</label>
              <div>
                <input id="ai-kb-input" value={kbInput} onChange={event => setKbInput(event.target.value)} placeholder="Например: доставка по СНГ от 500 ₽" />
                <button type="submit" className="btn btn-primary" disabled={!kbInput.trim()}>Добавить</button>
              </div>
            </form>

            <div className="ai-kb-list" aria-label="Факты базы знаний">
              {kbItems.length === 0 ? (
                <div className="ai-empty-knowledge"><BookOpen size={20} aria-hidden="true" /><span>Факты помогают агенту отвечать точнее</span></div>
              ) : kbItems.map(item => (
                <div key={item.id} className="ai-kb-row">
                  <span>{item.text}</span>
                  <button type="button" onClick={() => removeKb(item.id)} aria-label={`Удалить факт: ${item.text}`}><Trash2 size={15} aria-hidden="true" /></button>
                </div>
              ))}
            </div>

            {runtime?.embeddings.enabled && kbIndex && (
              <div className="ai-index-state"><span className="ai-live-dot is-on" aria-hidden="true" />Проиндексировано: {kbReady}{kbPending > 0 && <span className="is-warn"> · ожидают: {kbPending}</span>}</div>
            )}

            <div className="ai-data-sharing">
              <div className="ai-data-sharing-head">
                <div><b>Внешняя языковая модель</b><small>{runtime?.organizationConsented ? 'Передача данных разрешена' : 'Работа только по локальным правилам'}</small></div>
                <span className="switch">
                  <input type="checkbox" aria-label="Разрешить передачу переписки внешней языковой модели" checked={!!runtime?.organizationConsented} disabled={consentBusy || (runtime ? !runtime.consentAvailable : true)} onChange={event => void setDataSharing(event.target.checked)} />
                  <span className="slider" />
                </span>
              </div>
              <details className="ai-data-details">
                <summary>Какие данные передаются <ChevronDown size={14} aria-hidden="true" /></summary>
                <div>
                  <p>{runtime && !runtime.consentAvailable ? 'Администратор установки запретил передачу данных внешним моделям.' : 'Без согласия сообщения клиентов не покидают вашу установку Clientix.'}</p>
                  {runtime && runtime.transmitted.length > 0 && <ul>{runtime.transmitted.map((item, index) => <li key={index}>{item}</li>)}</ul>}
                  {runtime?.organizationConsented && consentDate && <p className="ai-consent-stamp">Согласие выдано {consentDate}{runtime.consentAcceptedBy ? ` · пользователь #${runtime.consentAcceptedBy}` : ''}</p>}
                </div>
              </details>
              {consentError && <div className="ai-consent-error" role="alert"><ShieldAlert size={14} aria-hidden="true" /> {consentError}</div>}
            </div>
          </div>
        </section>

        <section className="ai-panel ai-playground-panel" aria-labelledby="ai-playground-title">
          <header className="ai-panel-head">
            <span className="ai-panel-icon"><MessageSquareText size={17} aria-hidden="true" /></span>
            <div><h2 id="ai-playground-title">Тестовый диалог</h2><p>Так агент ответит клиенту</p></div>
            <span className="ai-free-test">Без расхода квоты</span>
          </header>
          <div className="ai-playground-context">
            <span className={`ai-live-dot ${local ? '' : 'is-on'}`} aria-hidden="true" />
            {local ? 'Локальные правила' : 'Внешняя модель'}<span>·</span>Каталог и база знаний подключены
          </div>
          <div className="ai-chat-canvas" ref={pgMessagesRef} aria-live="polite">
            {pgMessages.map((message, index) => (
              <div key={index} className={`pg-msg ${message.from}`} dangerouslySetInnerHTML={{ __html: formatMessageHTML(message.text) }} />
            ))}
            <div ref={pgEndRef} />
          </div>
          <form className="ai-chat-composer" onSubmit={(event) => { event.preventDefault(); pgSend(); }}>
            <label htmlFor="ai-playground-input">Сообщение для проверки</label>
            <div>
              <input id="ai-playground-input" value={pgInput} onChange={event => setPgInput(event.target.value)} placeholder="Например: есть ли скидка на опт?" />
              <button type="submit" disabled={!pgInput.trim()} aria-label="Отправить тестовое сообщение"><Send size={18} aria-hidden="true" /></button>
            </div>
          </form>
        </section>
      </div>
    </section>
  );
}
